import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import PolicyLibraryItem, { PolicyItem } from '../../Components/PolicyComparison/PolicyLibraryItem';
import DropZone from '../../Components/PolicyComparison/DropZone';
import CustomSelect from '../../Components/Dropdown';
import styles from './style.module.scss';
import PageContainer from '../PageContainer';
import { getPolicyClauseList, getThemes, policyComparison } from '../../api/common';
import FullComparison from "../../icons/fullComparison.svg?react";
import ClauseComparison from "../../icons/clauseComparison.svg?react";
import CustomComparison from "../../icons/customSelection.svg?react";
import ComparisonIcon from "../../icons/compareIcon.svg?react";

const PolicyComparison: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [basePolicy, setBasePolicy] = useState<PolicyItem | null>(null);
  const [comparePolicies, setComparePolicies] = useState<PolicyItem[]>([]);
  const [comparisonMode, setComparisonMode] = useState<'full' | 'clause' | 'custom'>('full');
  const [selectedClause, setSelectedClause] = useState<Array<{ label: string; value: string }>>([]);
  const [customText, setCustomText] = useState('');
  const [themeList, setThemeList] = useState<any>();
  const [showResults, setShowResults] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [clauseList, setClauseList] = useState([]);


  useEffect(() => {
    getThemes().then((response) => {
      setThemeList(response);
    })
  }, [])

  const data = themeList?.map((item: any) => ({
    id: item?.themeName?.toLowerCase(),
    themeid: item?.themeId,
    name: item?.themeName,
    themeCode: item?.themeCode,
    type: item?.type,
    country: item?.countryCode ?? "",
    description: item?.themeDescription,
    version: item?.type
  })) || [];


  // Get IDs of items that have been dropped
  const droppedItemIds = [
    basePolicy?.id,
    ...comparePolicies.map(p => p.id)
  ].filter(Boolean);

  // Filter available items (not yet dropped)
  const availableItems = data.filter((item: any) => !droppedItemIds.includes(item.id));

  // Filter by search query
  const filteredItems = searchQuery
    ? availableItems.filter((item: any) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : availableItems;

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    // Only allow dragging from library
    if (source.droppableId !== 'library') {
      return;
    }

    const draggedItem = filteredItems[source.index];
    if (!draggedItem) return;

    if (destination.droppableId === 'base-policy') {
      // Base policy can only have one item
      setBasePolicy(draggedItem);
    } else if (destination.droppableId === 'compare-policy') {
      // Compare can have multiple items
      if (!comparePolicies.find(p => p.id === draggedItem.id)) {
        setComparePolicies([...comparePolicies, draggedItem]);
      }
    }
  };

  const handleClearBase = () => {
    setBasePolicy(null);
  };

  const handleClearCompare = (itemId: string) => {
    setComparePolicies(comparePolicies.filter(p => p.id !== itemId));
  };
  const truncateWords = (text: any, limit = 10) => {
    const words = text.split(" ");
    return words.length > limit
      ? words.slice(0, limit).join(" ") + "..."
      : text;
  };

  const handleClauseClick = () => {
    getPolicyClauseList(basePolicy?.name).then((response) => {
      const responseMappeddata = response.map((data: any) => {
        return {
          label: `${data.titleId}  ${truncateWords(data.title, 10)}`,
          value: data.titleId
        };
      });
      setClauseList(responseMappeddata);

    })
  }

  // Helper function to parse numbered string into array
  const parseNumberedString = (text: string): string[] => {
    if (!text) return [];

    // Split by newline and filter out empty lines
    const lines = text.split('\n').filter(line => line.trim());

    // Remove numbering (1., 2., etc.) and trim
    return lines.map(line => line.replace(/^\d+\.\s*/, '').trim());
  };

  const handleCompare = async () => {
    if (basePolicy && comparePolicies.length > 0) {
      setIsLoading(true);
      setShowResults(false);

      try {
        const formData = {
          basePolicy: basePolicy.themeCode,
          comparePolicies: comparePolicies.map(p => p.themeCode),
          comparisonMode: comparisonMode === "full" ? "ENTIRE_POLICY" : comparisonMode === "clause" ? "CLAUSE" : "CUSTOM",
          customInput: comparisonMode === "custom" ? customText : comparisonMode === "clause" ? selectedClause.map((data: any) => data.value).join(", ") : null
        };

        const response = await policyComparison(formData);
        if (response?.results?.[0]?.completionResults) {
          setComparisonResults(response.results[0].completionResults);
          setShowResults(true);
        }
      } catch (error) {
        console.error('Comparison failed:', error);
        // Handle error - you might want to show an error message to the user
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isCompareDisabled = !basePolicy || comparePolicies.length === 0;

  return (
    <PageContainer>
      <div className={styles.policyComparison}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>Policy Comparison</h1>
          <p className={styles.pageDescription}>
            Drag and drop policies into the comparison areas to view detailed insights, side-by-side analytics, and improvement opportunities.
          </p>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <div className={styles.content}>
            {/* Left Sidebar - Policy Library */}
            <div className={styles.sidebar}>
              <div className={styles.sidebarHeader}>
                <h2 className={styles.sidebarTitle}>Policy</h2>
                <span className={styles.countBadge}>{data?.length} Policies</span>
              </div>

              {/* Search */}
              <div className={styles.searchContainer}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.searchIcon}>
                  <circle cx="7" cy="7" r="5" stroke="#94A3B8" strokeWidth="1.5" />
                  <path d="M11 11L14 14" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Search Policy"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <Droppable droppableId="library">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={styles.libraryList}
                  >
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item: any, index: any) => (
                        <PolicyLibraryItem key={item.id} item={item} index={index} />
                      ))
                    ) : (
                      <div className={styles.emptyLibrary}>
                        <p>No items found</p>
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Main Content - Drop Zones */}
            <div className={styles.mainContent}>
              <div className={styles.dropZones}>
                <div className={styles.dropZoneWrapper}>
                  <DropZone
                    id="base-policy"
                    title="Base Policy"
                    description="Drag a policy from the left panel and drop it here to set your base policy."
                    droppedItems={basePolicy ? [basePolicy] : []}
                    onClear={handleClearBase}
                    isSingle={true}
                  />
                </div>

                <div className={styles.dropZoneWrapper}>
                  <DropZone
                    id="compare-policy"
                    title="Policies to Compare"
                    description="Drag a policy from the left panel and drop it here to compare against the base policy."
                    droppedItems={comparePolicies}
                    onClear={handleClearCompare}
                    isSingle={false}
                    variant="compare"
                  />
                </div>
              </div>

              {/* Comparison Mode Section */}
              <div className={styles.comparisonMode}>
                <h3 className={styles.comparisonModeTitle}>Comparison Mode</h3>
                <p className={styles.comparisonModeDescription}>
                  Choose the level of comparison.
                </p>
                <div className={styles.comparisonModeButtons}>
                  <button
                    className={`${styles.modeButton} ${comparisonMode === 'full' ? styles.active : ''} ${isCompareDisabled ? styles.disabled : ''}`}
                    onClick={() => !isCompareDisabled && setComparisonMode('full')}
                    disabled={isCompareDisabled}
                  >
                    <FullComparison />
                    Full Comparison
                  </button>
                  <button
                    className={`${styles.modeButton} ${comparisonMode === 'clause' ? styles.active : ''} ${isCompareDisabled ? styles.disabled : ''}`}
                    onClick={() => {
                      if (!isCompareDisabled) {
                        setComparisonMode('clause');
                        handleClauseClick();
                      }
                    }}

                    disabled={isCompareDisabled}
                  >
                    <ClauseComparison />
                    Clause Comparison
                  </button>
                  <button
                    className={`${styles.modeButton} ${comparisonMode === 'custom' ? styles.active : ''} ${isCompareDisabled ? styles.disabled : ''}`}
                    onClick={() => !isCompareDisabled && setComparisonMode('custom')}
                    disabled={isCompareDisabled}
                  >
                    <CustomComparison />
                    Custom Comparison
                  </button>
                </div>

                {/* Clause Comparison - Select Clause Section */}
                {comparisonMode === 'clause' && !isCompareDisabled && (
                  <div className={styles.clauseSection}>
                    {/* <label className={styles.clauseLabel}>Select Clause / Section</label> */}
                    <CustomSelect
                      label={"Select Clause / Section"}
                      options={clauseList}
                      placeholder="E.g., 7.1 Technology Risk Management"
                      value={selectedClause}
                      onChange={(selected) => setSelectedClause(selected)}
                      multiSelect={false}
                      searchable={true}
                      selectContainerClassName={styles.customSelectContainer}
                    />
                    <p className={styles.clauseHint}>
                      💡 Select a specific clause to perform a focused comparison.
                    </p>
                  </div>
                )}

                {/* Custom Comparison - Enter Custom Text Section */}
                {comparisonMode === 'custom' && !isCompareDisabled && (
                  <div className={styles.customSection}>
                    <label className={styles.customLabel}>Enter Custom Text</label>
                    <textarea
                      className={styles.customTextarea}
                      placeholder="Type your custom keywords, e.g., Compare only sections related to privacy."
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      rows={1}
                    />
                    <p className={styles.customHint}>
                      💡 Enter text to perform a custom comparison across policies.
                    </p>
                  </div>
                )}
              </div>

              {/* Compare Button */}
              <div className={styles.actionBar}>
                <button
                  className={`${styles.compareButton} ${isCompareDisabled || isLoading ? styles.disabled : ''}`}
                  onClick={handleCompare}
                  disabled={isCompareDisabled || isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className={styles.spinner}></div>
                      Comparing...
                    </>
                  ) : (
                    <>
                      <ComparisonIcon />
                      Compare
                    </>
                  )}
                </button>
              </div>

              {/* Comparison Results Section */}
              {showResults && comparisonResults && (
                <div className={styles.resultsSection}>
                  <div className={styles.resultsHeader}>
                    <h3 className={styles.resultsTitle}>Comparison Results</h3>
                    <button className={styles.closeResults} onClick={() => { setShowResults(false); setComparisonResults(null) }}>×</button>
                  </div>

                  {/* Key Findings & Recommendations */}
                  {comparisonResults.keyFindingsAndRecommendations && (
                    <div className={styles.resultCard}>
                      <div className={styles.cardHeader}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 2L3 7V11C3 15.5 6.5 19.5 10 20.5C13.5 19.5 17 15.5 17 11V7L10 2Z" fill="#1A2353" />
                        </svg>
                        <h4 className={styles.cardTitle}>Key Findings & Recommendations</h4>
                      </div>
                      <div className={styles.cardContent}>
                        <div className={styles.finding}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1L10.5 6L16 7L12 11L13 16L8 13.5L3 16L4 11L0 7L5.5 6L8 1Z" fill="#10B981" />
                          </svg>
                          <div>
                            <strong>Improvement Summary</strong>
                            {(() => {
                              const summary = comparisonResults.keyFindingsAndRecommendations.improvementSummary;
                              if (typeof summary === 'string' && summary.includes('\n')) {
                                const items = parseNumberedString(summary);
                                return (
                                  <ul className={styles.improvementList}>
                                    {items.map((item: string, index: number) => (
                                      <li key={index}>{item}</li>
                                    ))}
                                  </ul>
                                );
                              } else {
                                return <p>{summary}</p>;
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Missing Controls */}
                  {comparisonResults.missingControls && (
                    <div className={styles.resultCard}>
                      <div className={styles.cardHeader}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 15V10M10 5H10.01M2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <h4 className={styles.cardTitle}>Missing Controls</h4>
                      </div>
                      <div className={styles.cardContent}>
                        <p className={styles.sectionDesc}>
                          {comparisonResults.missingControls.overallSummary}
                        </p>

                        <div className={styles.controlList}>
                          {comparisonResults.missingControls.controlGaps?.map((gap: any, index: number) => (
                            <div key={index} className={styles.controlItem}>
                              <span className={styles.severity} data-severity={gap.severity.toLowerCase()}>
                                {gap.severity}-severity gaps
                              </span>
                              &nbsp;&nbsp;<strong>{gap.label}</strong>
                              <div className={styles.severityDescription}>
                                <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>{gap.details}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Potential Remediations */}
                  {comparisonResults.potentialRemediations && comparisonResults.potentialRemediations.length > 0 && (
                    <div className={styles.resultCard}>
                      <div className={styles.cardHeader}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M9 2C9 2 9 3 9 5C9 7 10 8 12 8C14 8 15 8 15 8M15 2L9 8" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
                          <rect x="3" y="10" width="14" height="8" rx="1" stroke="#3B82F6" strokeWidth="2" />
                        </svg>
                        <h4 className={styles.cardTitle}>Potential Remediations</h4>
                      </div>
                      <div className={styles.cardContent}>
                        <div className={styles.remediationList}>
                          {comparisonResults.potentialRemediations.map((remediation: any) => (
                            <div key={remediation.id} className={styles.remediationItem}>
                              <span className={styles.remediationNumber}>{remediation.id}</span>
                              <p>{remediation.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Base Policy Performs Better In */}
                  {comparisonResults.betterIn && (
                    <div className={styles.resultCard}>
                      <div className={styles.cardHeader}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 2L12 7L17 8L13.5 11.5L14.5 17L10 14.5L5.5 17L6.5 11.5L3 8L8 7L10 2Z" fill="#10B981" />
                        </svg>
                        <h4 className={styles.cardTitle}>{basePolicy?.name} Performs Better In</h4>
                      </div>
                      <div className={styles.cardContent}>
                        <p className={styles.sectionDesc}>
                          {comparisonResults.betterIn.summary}
                        </p>

                        <div className={styles.performanceList}>
                          {comparisonResults.betterIn.points?.map((point: string, index: number) => (
                            <div key={index} className={styles.performanceItem}>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="7" fill="#10B981" />
                                <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                              <span>{point}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* High-Risk Gaps Requiring Immediate Attention */}
                  {comparisonResults.highRiskGaps && comparisonResults.highRiskGaps.length > 0 && (
                    <div className={styles.resultCard}>
                      <div className={styles.cardHeader}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 2L2 17H18L10 2Z" fill="#EF4444" />
                          <path d="M10 7V11M10 13V14" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <h4 className={styles.cardTitle}>High-Risk Gaps Requiring Immediate Attention</h4>
                      </div>
                      <div className={styles.cardContent}>
                        <div className={styles.riskList}>
                          {comparisonResults.highRiskGaps.map((gap: any, index: number) => (
                            <div key={index} className={styles.riskItem}>
                              <div className={styles.riskHeader}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M10 2L2 17H18L10 2Z" fill="#EF4444" />
                                  <path d="M10 7V11M10 13V14" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                <strong>{gap.title}</strong>
                              </div>
                              <p>{gap.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </DragDropContext>
      </div>
    </PageContainer>
  );
};

export default PolicyComparison;
